import { useEffect, useRef, useState } from 'react';
import { useMethods } from '@lhb/hook';
import { BAILIAN_COMPARE_AGENT_ID, getBailianApiKey } from './ai-config';
import { AIComparePoint, buildAIEvaluationInput, parseAIEvaluationResponse } from './utils';

/** 百炼接口响应较慢，超时时间设置为 300 秒 */
const AI_REQUEST_TIMEOUT = 300 * 1000;

/**
 * @description 多机会点对比的 AI 评价请求
 * 每次对比组变化（version 递增）重新生成一次，不做缓存
 * @param points 当前参与对比的全部点位
 * @param version 对比数据版本，仅在对比接口成功返回后递增
 */
export function useAIEvaluation(points: AIComparePoint[], version: number) {
  const [comments, setComments] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const controllerRef = useRef<AbortController | null>(null);
  const requestRef = useRef(0);

  const methods = useMethods({
    /**
     * @description 请求百炼平台生成对比组 AI 评价
     */
    load() {
      if (points.length < 2) return;
      const requestId = ++requestRef.current;
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      let timedOut = false;
      const timer = window.setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, AI_REQUEST_TIMEOUT);
      setLoading(true);
      setFailed(false);
      setComments({});

      const targetUrl = `https://dashscope.aliyuncs.com/api/v2/apps/agent/${BAILIAN_COMPARE_AGENT_ID}/compatible-mode/v1/responses`;
      const input = JSON.stringify(buildAIEvaluationInput(points));

      fetch(targetUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getBailianApiKey()}`,
        },
        body: JSON.stringify({
          input,
          stream: false,
        }),
        signal: controller.signal,
      }).then((response) => {
        if (!response.ok) throw new Error(`AI 评价请求失败：${response.status}`);
        return response.json();
      }).then((response: any) => {
        if (requestId !== requestRef.current) return;
        if (response?.status && response.status !== 'completed') {
          throw new Error('AI 评价生成未完成');
        }
        setComments(parseAIEvaluationResponse(response));
        setFailed(false);
      }).catch((error: any) => {
        if (requestId !== requestRef.current) return;
        // 主动取消（切换对比组/关闭抽屉）不展示失败态
        if (error?.name === 'AbortError' && !timedOut) return;
        console.log(error);
        setComments({});
        setFailed(true);
      }).finally(() => {
        window.clearTimeout(timer);
        if (requestId === requestRef.current) setLoading(false);
      });
    },
  });

  // 仅在对比数据版本变化时重新生成，避免乐观删除的中间态触发多次请求
  useEffect(() => {
    if (!version || points.length < 2) return;
    methods.load();
  }, [version]);

  // 组件卸载时取消未完成的请求，避免关闭抽屉后仍更新状态
  useEffect(() => () => {
    requestRef.current += 1;
    controllerRef.current?.abort();
  }, []);

  return {
    comments,
    loading,
    failed,
  };
}
